import Icon from "@/Components/Icon/Icon";
import Spinner from "@/Components/Spinner/Spinner";
import SettingsCard from "@/Components/WebsiteSettings/SettingsCard";
import { useSetModal } from "@/shared/atoms/useModal";
import {
  lazy,
  Suspense,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type ReactQuillType from "react-quill";
import "react-quill/dist/quill.snow.css";
const ReactQuill = lazy(() => import("react-quill"));

const toolbar = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link", "image"],
  ["clean"],
];

const removeBase64Images = (_node: Node, delta) => {
  delta.ops = delta.ops.filter(
    (op) => !String(op.insert?.image ?? "").startsWith("data:")
  );
  return delta;
};

const withImageTransformation = (url: string) =>
  url.replace("/upload/", `/upload/c_limit,w_1200/f_auto/q_auto/`);

const CONTENT_STYLES = `
  h1 { font-size: 2em; font-weight: bold; }
  h2 { font-size: 1.5em; font-weight: bold; }
  h3 { font-size: 1.17em; font-weight: bold; }
  img { max-width: 100%; max-height: 600px; width: auto; height: auto; }
`;

const toHtmlDocument = (body: string) =>
  `<!DOCTYPE html><html><head><meta charset="utf-8"><style>${CONTENT_STYLES}</style></head><body>${body}</body></html>`;

const parseHtml = (html: string) =>
  new DOMParser().parseFromString(html, "text/html");

const isEmptyContent = (html: string) => {
  const { body } = parseHtml(html);
  return !body.textContent.trim() && !body.querySelector("img");
};

interface AboutUsProps {
  defaultValue?: string;
}

const AboutUs = ({ defaultValue }: AboutUsProps) => {
  const { t } = useTranslation("");
  const { register, setValue, control } = useFormContext();

  const setModal = useSetModal();
  const quillRef = useRef<ReactQuillType>(null);
  const [initialContent] = useState(() =>
    defaultValue ? parseHtml(defaultValue).body.innerHTML : ""
  );
  const [content, setContent] = useState(initialContent);
  const deferredContent = useDeferredValue(content);

  useEffect(() => {
    setValue(
      "about_us_content",
      isEmptyContent(deferredContent) ? "" : toHtmlDocument(deferredContent)
    );
  }, [deferredContent, register, setValue]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: toolbar,
        handlers: {
          image: () => {
            const index =
              quillRef.current?.getEditor().getSelection(true)?.index ?? 0;
            setModal("aboutUsImage", {
              onUpload: (url: string) => {
                const editor = quillRef.current?.getEditor();
                editor?.insertEmbed(
                  index,
                  "image",
                  withImageTransformation(url),
                  "user"
                );
                editor?.setSelection(index + 1, 0, "user");
              },
            });
          },
        },
      },
      clipboard: {
        matchers: [["IMG", removeBase64Images]],
      },
    }),
    []
  );

  return (
    <SettingsCard
      icon="info"
      title={t("settings_page.aboutUs")}
      description={t("settings_page.cards.aboutUsDescription")}
    >
      <div className="mb-6 flex items-start gap-4 rounded-xl border border-primary/20 bg-primary/5 p-5 text-small text-text-secondary">
        <Icon className="mt-0.5 size-7 shrink-0 fill-primary" variants="info" />
        <div>{t("settings_page.aboutUsInfo")}</div>
      </div>

      <Suspense fallback={<Spinner size="0.25rem" />}>
        <Controller
          name="about_us_content"
          control={control}
          defaultValue={defaultValue}
          render={({ value }) => (
            <ReactQuill
              ref={quillRef}
              theme="snow"
              defaultValue={value}
              onChange={setContent}
              modules={modules}
              className="overflow-hidden rounded-xl border border-grey-blue-light [&_.ql-container]:border-0 [&_.ql-editor]:min-h-120 [&_.ql-editor_img]:max-h-200 [&_.ql-toolbar]:border-0 [&_.ql-toolbar]:border-b [&_.ql-toolbar]:border-grey-blue-light [&_.ql-toolbar]:bg-grey-secondary"
            />
          )}
        />
      </Suspense>
    </SettingsCard>
  );
};

export default AboutUs;
